import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const JobCard = ({ job, onSaveJob, isSaved }) => {
  const navigate = useNavigate();

  const handleApply = () => {
    navigate('/job-details-and-applications', { state: { jobId: job?.id, action: 'apply' } });
  };

  const handleViewDetails = () => {
    navigate('/job-details-and-applications', { state: { jobId: job?.id } });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[priority] || colors?.low;
  };

  const getWorkTypeIcon = (type) => {
    const icons = {
      electrical: 'Zap',
      hvac: 'Wind',
      plumbing: 'Droplet',
      solar: 'Sun',
      general: 'Wrench'
    };
    return icons?.[type] || 'Wrench';
  };

  const formatDeadline = (deadline) => {
    const date = new Date(deadline);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Verlopen';
    if (diffDays === 0) return 'Vandaag';
    if (diffDays === 1) return 'Morgen';
    return `${diffDays} dagen`;
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden hover:elevation-md transition-smooth"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name={getWorkTypeIcon(job?.workType)} size={24} color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1 line-clamp-2">
                {job?.title}
              </h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Icon name="Building2" size={14} />
                <span className="truncate">{job?.clientCompany}</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => onSaveJob(job?.id)}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
            aria-label={isSaved ? 'Verwijder uit opgeslagen' : 'Opslaan'}
          >
            <Icon 
              name={isSaved ? 'Bookmark' : 'Bookmark'} 
              size={20} 
              color={isSaved ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
              fill={isSaved ? 'var(--color-accent)' : 'none'}
            />
          </button>
        </div>

        <div className="space-y-2.5 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="MapPin" size={16} className="flex-shrink-0" />
            <span className="truncate">{job?.location}</span>
            <span className="text-xs bg-muted px-2 py-0.5 rounded whitespace-nowrap">
              {job?.distance} km
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Icon name="Euro" size={16} className="flex-shrink-0 text-muted-foreground" />
            <span className="font-semibold text-foreground">
              €{job?.hourlyRate?.toFixed(2)}/uur
            </span>
            {job?.estimatedHours && (
              <span className="text-muted-foreground">
                • ~{job?.estimatedHours} uur
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} className="flex-shrink-0" />
            <span>Start: {new Date(job.startDate)?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {job?.requiredSkills?.slice(0, 3)?.map((skill, index) => (
            <span
              key={index}
              className="caption bg-primary/10 text-primary px-2.5 py-1 rounded-md"
            >
              {skill}
            </span>
          ))}
          {job?.requiredSkills?.length > 3 && (
            <span className="caption bg-muted text-muted-foreground px-2.5 py-1 rounded-md">
              +{job?.requiredSkills?.length - 3}
            </span>
          )}
        </div>

        {job?.affiliateInfo && (
          <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg mb-4">
            <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Users" size={16} color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="caption text-muted-foreground">Aangebracht door</p>
              <p className="text-sm font-medium text-foreground truncate">
                {job?.affiliateInfo?.name}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <span className={`caption px-2.5 py-1 rounded-md border ${getPriorityColor(job?.priority)}`}>
              {job?.priority === 'high' ? 'Urgent' : job?.priority === 'medium' ? 'Normaal' : 'Laag'}
            </span>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span className="caption">{formatDeadline(job?.applicationDeadline)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewDetails}
            fullWidth
          >
            Meer Info
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleApply}
            iconName="Send"
            iconPosition="right"
            fullWidth
          >
            Solliciteren
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobCard;